import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../Contexts/AuthContext";
import { customerService } from "../../../Api/Services/customerService";
import { individualPersonService } from "../../../Api/Services/individualPersonService";
import { legalEntityService } from "../../../Api/Services/legalEntityService";
import { productCategoryService } from "../../../Api/Services/productCategoryService";
import { productCategoryDiscountService } from "../../../Api/Services/productCategoryDiscountService";
import { Customer } from "../../../Models/Customer";
import { ProductCategory } from "../../../Models/ProductCategory";
import { ProductCategoryDiscount } from "../../../Models/ProductCategoryDiscount";
import { SimplifiedUserInfo } from "../../../Models/User";
import { getGenderLabel } from "../../../Utils/Enums";
import {
  formatCpf,
  formatCnpj,
  formatDateToBR,
  formatPhoneNumber,
  unformat,
} from "../../../Utils/DataFormatters";
import { Select } from "../../../Components/Select/Select";
import { Input } from "../../../Components/Input/Input";
import { FaArrowLeft, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import Button from "../../../Components/Button/Button";

const AdminCustomerDetailsPage = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const { isAdmin, isOperator } = useAuth();

  const basePath = isAdmin ? "/admin" : "/operador";

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [discounts, setDiscounts] = useState<ProductCategoryDiscount[]>([]);
  const [user, setUser] = useState<SimplifiedUserInfo | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  const [showDiscountForm, setShowDiscountForm] = useState<boolean>(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [newCategoryId, setNewCategoryId] = useState<string | null>(null);
  const [newDiscountPercentage, setNewDiscountPercentage] = useState<number>(0);
  const [cpfValue, setCpfValue] = useState<string>("");
  const [cnpjValue, setCnpjValue] = useState<string>("");
  const [stateRegistrationValue, setStateRegistrationValue] =
    useState<string>("");
  const [isStateRegistrationExempt, setIsStateRegistrationExempt] =
    useState<boolean>(false);
  const [isSavingCpf, setIsSavingCpf] = useState<boolean>(false);
  const [isSavingCnpj, setIsSavingCnpj] = useState<boolean>(false);
  const [isSavingStateRegistration, setIsSavingStateRegistration] =
    useState<boolean>(false);
  const [isEditingCpf, setIsEditingCpf] = useState<boolean>(false);
  const [isEditingCnpj, setIsEditingCnpj] = useState<boolean>(false);
  const [isEditingStateRegistration, setIsEditingStateRegistration] =
    useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    loadCustomer(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadData();
  }, [customer]);

  const loadData = async () => {
    const categories = await productCategoryService.getAll();
    setCategories(categories);
  };

  const reloadDiscounts = async () => {
    if (!customer?.id) {
      return;
    }
    const updatedDiscounts = await productCategoryDiscountService.getByCustomer(
      { customerId: customer.id },
    );
    setDiscounts(updatedDiscounts);
  };

  const handleSaveDiscount = async () => {
    if (!newCategoryId || isNaN(Number(newDiscountPercentage)) || !customer) {
      toast.warn(
        "Por favor, preencha todos os campos antes de salvar o desconto.",
      );
      return;
    }

    if (
      discounts.some((d) => d.categoryId === newCategoryId && d.id !== editId)
    ) {
      toast.warn(
        "Esse cliente já possui desconto para essa categoria.",
      );
      return;
    }

    if (editId) {
      const updatedDiscountCategoryId = discounts.find(
        (d) => d.id === editId,
      )?.categoryId!!;

      const updated = await productCategoryDiscountService.update({
        id: editId,
        customerId: customer?.id,
        categoryId: updatedDiscountCategoryId,
        discountPercentage: newDiscountPercentage,
      });

      if (updated) {
        toast.success(
          "Desconto atualizado com sucesso!",
        );
      }
    } else {
      const created = await productCategoryDiscountService.create({
        customerId: customer.id,
        categoryId: newCategoryId,
        discountPercentage: newDiscountPercentage,
      });

      if (created) {
        toast.success(
          "Desconto adicionado com sucesso!",
        );
      }
    }

    setNewCategoryId(null);
    setNewDiscountPercentage(0);
    setEditId(null);
    setShowDiscountForm(false);
    reloadDiscounts();
  };

  const handleDeleteDiscount = async (id: string) => {
    const deleted = await productCategoryDiscountService.delete(id);

    if (deleted) {
      toast.success(
        "Desconto removido com sucesso!",
      );
      reloadDiscounts();
    }
  };

  const loadCustomer = async (id: string) => {
    try {
      const data = await customerService.get(id);
      if (!data) {
        toast.error("Cliente não encontrado.");
        navigate("/admin/clientes", { replace: true });
      } else {
        setCustomer(data);
        setDiscounts(data.categoryDiscounts ?? []);
        setUser(data.user);

        if (data.individualPerson) {
          setCpfValue(formatCpf(data.individualPerson.cpf ?? ""));
        }
        if (data.legalEntity) {
          setCnpjValue(formatCnpj(data.legalEntity.cnpj ?? ""));
          setStateRegistrationValue(data.legalEntity.stateRegistration ?? "");
          setIsStateRegistrationExempt(
            !!data.legalEntity.isStateRegistrationExempt,
          );
        }
      }
    } catch (error) {
      toast.error("Erro ao carregar cliente.");
      navigate("/admin/clientes", { replace: true });
    }
  };

  const handleSaveCpf = async () => {
    if (!customer?.individualPerson) {
      return;
    }

    const cpfDigits = unformat(cpfValue);
    if (!cpfDigits) {
      toast.error("Informe um CPF.");
      return;
    }
    if (cpfDigits.length !== 11) {
      toast.error("CPF deve ter 11 dígitos.");
      return;
    }

    try {
      setIsSavingCpf(true);
      await individualPersonService.updateDocument(
        customer.individualPerson.id,
        {
          cpf: cpfDigits,
        },
      );
      toast.success(
        "CPF atualizado com sucesso.",
      );
      setIsEditingCpf(false);
      await loadCustomer(customer.id);
    } catch (error) {
      if ((error as any)?.response?.status === 409) {
        toast.error(
          "CPF já cadastrado para outro cliente.",
        );
      } else if ((error as any)?.response?.status === 400) {
        toast.error(
          (error as any)?.response?.data ?? "CPF inválido.",
        );
      } else {
        toast.error("Erro ao atualizar CPF.");
      }
    } finally {
      setIsSavingCpf(false);
    }
  };

  const resetLegalDocumentFields = () => {
    if (customer?.legalEntity) {
      setCnpjValue(formatCnpj(customer.legalEntity.cnpj ?? ""));
      setStateRegistrationValue(customer.legalEntity.stateRegistration ?? "");
      setIsStateRegistrationExempt(
        !!customer.legalEntity.isStateRegistrationExempt,
      );
    }
  };

  const handleSaveCnpj = async () => {
    if (!customer?.legalEntity) {
      return;
    }

    const cnpjDigits = unformat(cnpjValue);
    if (!cnpjDigits) {
      toast.error("Informe um CNPJ.");
      return;
    }
    if (cnpjDigits.length !== 14) {
      toast.error("CNPJ deve ter 14 dígitos.");
      return;
    }

    try {
      setIsSavingCnpj(true);
      await legalEntityService.updateCnpj(customer.legalEntity.id, {
        cnpj: cnpjDigits,
      });
      toast.success(
        "CNPJ atualizado com sucesso.",
      );
      setIsEditingCnpj(false);
      await loadCustomer(customer.id);
    } catch (error) {
      if ((error as any)?.response?.status === 409) {
        toast.error(
          "CNPJ já cadastrado para outra empresa.",
        );
      } else if ((error as any)?.response?.status === 400) {
        toast.error(
          (error as any)?.response?.data ??
            "CNPJ inválido.",
        );
      } else {
        toast.error("Erro ao atualizar CNPJ.");
      }
    } finally {
      setIsSavingCnpj(false);
    }
  };

  const handleSaveStateRegistration = async () => {
    if (!customer?.legalEntity) {
      return;
    }
    try {
      setIsSavingStateRegistration(true);
      await legalEntityService.updateStateRegistration(
        customer.legalEntity.id,
        {
          stateRegistration: isStateRegistrationExempt
            ? null
            : stateRegistrationValue,
          isStateRegistrationExempt,
        },
      );
      toast.success(
        "Inscrição estadual atualizada com sucesso.",
      );
      setIsEditingStateRegistration(false);
      await loadCustomer(customer.id);
    } catch (error) {
      if ((error as any)?.response?.status === 400) {
        toast.error(
          (error as any)?.response?.data ??
            "Inscrição estadual inválida.",
        );
      } else {
        toast.error(
          "Erro ao atualizar inscrição estadual.",
        );
      }
    } finally {
      setIsSavingStateRegistration(false);
    }
  };

  return (
    <div>
      <SmartNavbar />
      <div className="p-4 sm:p-14">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 my-6 sm:my-0 sm:mb-4">
          <PageTitle>
            {"DETALHES DO CLIENTE"}
          </PageTitle>
          <Button
            onClick={() => navigate(`${basePath}/clientes`)}
            className="w-full sm:w-auto flex justify-center items-center gap-1 px-4 py-2"
          >
            <FaArrowLeft /> Voltar para lista de clientes
          </Button>
        </div>
        {customer ? (
          <div className="bg-white p-3 sm:p-6 rounded shadow">
            <h2 className="text-xl font-semibold mb-4">
              {"Tipo"}:{" "}
              {customer.customerType === 0
                ? "Pessoa Física"
                : "Pessoa Jurídica"}
            </h2>

            {customer.individualPerson && (
              <div className="mb-4">
                <p>
                  <strong>Nome:</strong> {customer.individualPerson.fullName}
                </p>
                {isAdmin ? (
                  <div
                    className={`flex items-center gap-2 ${isEditingCpf ? "my-3" : ""}`}
                  >
                    {isEditingCpf ? (
                      <div className="flex flex-col sm:flex-row sm:items-end gap-2 w-full">
                        <Input
                          label="CPF"
                          type="text"
                          value={cpfValue}
                          onChange={(e) => {
                            setCpfValue(formatCpf(unformat(e.target.value)));
                          }}
                          className="flex-1"
                        />
                        <div className="flex gap-2">
                          <Button variant="secondary"
                            className="h-12 min-w-[100px] px-4 text-sm"
                            onClick={() => {
                              setCpfValue(
                                formatCpf(customer.individualPerson?.cpf ?? ""),
                              );
                              setIsEditingCpf(false);
                            }}
                            disabled={isSavingCpf}
                          >
                            CANCELAR
                          </Button>
                          <Button isLoading={isSavingCpf && isEditingCpf}
                            className="h-12 min-w-[100px] px-4 text-sm"
                            onClick={handleSaveCpf}
                            disabled={isSavingCpf}
                          >
                            {"SALVAR"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p>
                          <strong>CPF:</strong>{" "}
                          {formatCpf(customer.individualPerson.cpf ?? "")}
                        </p>
                        <button
                          className="text-primary-600 hover:text-primary-700 text-sm font-semibold"
                          onClick={() => setIsEditingCpf(true)}
                        >
                          <FaEdit />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <p>
                    <strong>CPF:</strong>{" "}
                    {formatCpf(customer.individualPerson.cpf ?? "")}
                  </p>
                )}
                <p>
                  <strong>Data de nascimento:</strong>{" "}
                  {formatDateToBR(customer.individualPerson.birthDate)}
                </p>
                <p>
                  <strong>Gênero:</strong>{" "}
                  {customer.individualPerson.gender != null
                    ? getGenderLabel(customer.individualPerson.gender)
                    : "Não informado"}
                </p>
              </div>
            )}

            {customer.legalEntity && (
              <div className="mb-10">
                <p>
                  <strong>Razão Social:</strong>{" "}
                  {customer.legalEntity.companyName}
                </p>
                <p>
                  <strong>Nome Fantasia:</strong>{" "}
                  {customer.legalEntity.tradeName}
                </p>
                {isAdmin ? (
                  <div className="flex flex-col">
                    <div
                      className={`flex items-center gap-2 ${isEditingCnpj ? "my-3" : ""}`}
                    >
                      {isEditingCnpj ? (
                        <div className="flex flex-col sm:flex-row sm:items-end gap-2 w-full">
                          <Input
                            label="CNPJ"
                            type="text"
                            value={cnpjValue}
                            onChange={(e) =>
                              setCnpjValue(formatCnpj(unformat(e.target.value)))
                            }
                            className="flex-1"
                          />
                          <div className="flex gap-2">
                            <Button variant="secondary"
                              className="h-12 min-w-[100px] px-4 text-sm"
                              onClick={() => {
                                setCnpjValue(
                                  formatCnpj(customer.legalEntity?.cnpj ?? ""),
                                );
                                setIsEditingCnpj(false);
                              }}
                              disabled={isSavingCnpj}
                            >
                              CANCELAR
                            </Button>
                            <Button isLoading={isSavingCnpj && isEditingCnpj}
                              className="h-12 min-w-[100px] px-4 text-sm"
                              onClick={handleSaveCnpj}
                              disabled={isSavingCnpj}
                            >
                              {"SALVAR"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p>
                            <strong>CNPJ:</strong>{" "}
                            {formatCnpj(customer.legalEntity?.cnpj ?? "")}
                          </p>
                          <button
                            className="text-primary-600 hover:text-primary-700 text-sm font-semibold"
                            onClick={() => setIsEditingCnpj(true)}
                          >
                            <FaEdit />
                          </button>
                        </>
                      )}
                    </div>

                    <div
                      className={`flex gap-2 ${isEditingStateRegistration ? "my-3" : ""}`}
                    >
                      {isEditingStateRegistration ? (
                        <div className="flex flex-col gap-3 w-full">
                          <div className="flex gap-2">
                            <Input
                              label="Inscrição Estadual"
                              type="text"
                              value={stateRegistrationValue ?? ""}
                              disabled={isStateRegistrationExempt}
                              onChange={(e) =>
                                setStateRegistrationValue(e.target.value)
                              }
                              className="flex-1"
                            />
                            <div className="flex items-end gap-2">
                              <Button variant="secondary"
                                className="h-12 min-w-[100px] px-4 text-sm"
                                onClick={() => {
                                  resetLegalDocumentFields();
                                  setIsEditingStateRegistration(false);
                                }}
                                disabled={isSavingStateRegistration}
                              >
                                CANCELAR
                              </Button>
                              <Button isLoading={isSavingStateRegistration && isEditingStateRegistration}
                                className="h-12 min-w-[100px] px-4 text-sm"
                                onClick={handleSaveStateRegistration}
                                disabled={isSavingStateRegistration}
                              >
                                {"SALVAR"}
                              </Button>
                            </div>
                          </div>

                          <Input
                            label="Isento de Inscrição Estadual"
                            type="checkbox"
                            name="isStateRegistrationExempt"
                            value={isStateRegistrationExempt}
                            onChange={(e) => {
                              const checked = (e.target as HTMLInputElement)
                                .checked;
                              setIsStateRegistrationExempt(checked);
                              if (checked) {
                                setStateRegistrationValue("");
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <>
                          <p>
                            <strong>Inscrição Estadual:</strong>{" "}
                            {customer.legalEntity.isStateRegistrationExempt
                              ? "Isento"
                              : customer.legalEntity.stateRegistration}
                          </p>
                          <button
                            className="text-primary-600 hover:text-primary-700 text-sm font-semibold"
                            onClick={() => setIsEditingStateRegistration(true)}
                          >
                            <FaEdit />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      <strong>CNPJ:</strong>{" "}
                      {formatCnpj(customer.legalEntity?.cnpj ?? "")}
                    </p>
                    <p>
                      <strong>Inscrição Estadual:</strong>{" "}
                      {customer.legalEntity?.isStateRegistrationExempt
                        ? "Isento"
                        : customer.legalEntity?.stateRegistration}
                    </p>
                  </>
                )}
              </div>
            )}

            {user && (
              <div className="mb-10">
                <p>
                  <strong>E-mail:</strong> {user.email}
                </p>
                <p>
                  <strong>Telefone celular:</strong>{" "}
                  {formatPhoneNumber(user.phoneNumber)}
                </p>
              </div>
            )}

            {customer.addresses && customer.addresses.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-medium mb-2">
                  {"Endereços"}
                </h3>
                <ul className="space-y-2">
                  {customer.addresses.map((address, index) => (
                    <li
                      key={address.id}
                      className="p-3 border rounded bg-gray-50"
                    >
                      <p>
                        <strong>Endereço {index + 1}:</strong>
                      </p>
                      <p>
                        <strong>Rua:</strong> {address.street}, {address.number}
                      </p>
                      <p>
                        <strong>Bairro:</strong> {address.neighborhood}
                      </p>
                      <p>
                        <strong>Cidade:</strong> {address.city} -{" "}
                        {address.state}
                      </p>
                      <p>
                        <strong>CEP:</strong> {address.postalCode}
                      </p>
                      {address.complement && (
                        <p>
                          <strong>Complemento:</strong> {address.complement}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8 sm:mt-10">
              <h3 className="text-lg mb-2">
                {"Descontos por categoria de produto"}
              </h3>

              {!isOperator && showDiscountForm ? (
                <div className="flex flex-col sm:flex-row gap-3 my-4">
                  <div className="flex-1 w-full">
                    <Select
                      label="Categoria"
                      name="categoryId"
                      value={newCategoryId ?? ""}
                      placeholder="Selecione uma categoria"
                      onChange={(e) => setNewCategoryId(e.target.value)}
                      required
                      options={categories.map((c) => ({
                        value: c.id,
                        label: c.name,
                      }))}
                    />
                  </div>

                  <div className="flex-1 w-full">
                    <Input
                      label="Desconto (%)"
                      name="newDiscountPercentage"
                      type="number"
                      value={newDiscountPercentage}
                      min={0}
                      max={100}
                      step={0.01}
                      onChange={(e) =>
                        setNewDiscountPercentage(parseFloat(e.target.value))
                      }
                      required
                    />
                  </div>

                  <div className="flex gap-2 items-end">
                    <Button variant="secondary"
                      onClick={() => {
                        setEditId(null);
                        setNewCategoryId(null);
                        setNewDiscountPercentage(0);
                        setShowDiscountForm(false);
                      }}
                      className="w-full sm:w-32 py-2 sm:py-3"
                    >
                      CANCELAR
                    </Button>

                    <Button
                      onClick={handleSaveDiscount}
                      className="w-full sm:w-32 py-2 sm:py-3"
                    >
                      {editId ? "SALVAR" : "ADICIONAR"}
                    </Button>
                  </div>
                </div>
              ) : (
                !isOperator && (
                  <button
                    className="flex items-center gap-1 my-2 text-primary-600 hover:text-primary-700"
                    onClick={() => setShowDiscountForm(true)}
                  >
                    <FaPlus /> Adicionar Desconto
                  </button>
                )
              )}

              {/* MOBILE CARDS */}
              <div className="flex flex-col gap-4 sm:hidden">
                {discounts.map((d, index) => (
                  <div
                    key={d.id}
                    className="border rounded p-4 shadow-sm bg-white"
                  >
                    <h2 className="font-semibold mb-2">Desconto {index + 1}</h2>
                    <p>
                      <span className="font-medium">Categoria:</span>{" "}
                      {d.category.name}
                    </p>
                    <p>
                      <span className="font-medium">Desconto:</span>{" "}
                      {d.discountPercentage}%
                    </p>
                    <button
                      className="text-primary-600 hover:underline flex items-center gap-1 mt-2"
                      onClick={() => {
                        setEditId(d.id);
                        setNewCategoryId(d.category.id);
                        setNewDiscountPercentage(d.discountPercentage);
                        setShowDiscountForm(true);
                      }}
                    >
                      <FaEdit /> Editar desconto
                    </button>
                    <button
                      className="text-primary-600 hover:underline flex items-center gap-1 mt-1"
                      onClick={() => handleDeleteDiscount(d.id)}
                    >
                      <FaTrash /> Excluir desconto
                    </button>
                  </div>
                ))}
              </div>

              {/* DESKTOP TABLE */}
              <table className="hidden sm:table w-full border-collapse border border-gray-300 text-left mb-4">
                <thead>
                  <tr className="bg-primary-120">
                    <th className="border p-3 text-center">Categoria</th>
                    <th className="border p-3 text-center">Desconto</th>
                    {!isOperator && (
                      <th className="border p-3 text-center">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {discounts.length === 0 ? (
                    <tr>
                      <td
                        className="border p-4 text-center text-gray-600"
                        colSpan={isOperator ? 2 : 3}
                      >
                        Nenhum desconto cadastrado para categorias.
                      </td>
                    </tr>
                  ) : (
                    discounts.map((d) => (
                      <tr key={d.id} className="hover:bg-gray-50">
                        <td className="border p-3 text-center">
                          {d.category.name}
                        </td>
                        <td className="border p-3 text-center">
                          {d.discountPercentage}%
                        </td>
                        {!isOperator && (
                          <td className="border p-3 text-center space-x-2">
                            <button
                              className="text-primary-600 hover:text-primary-700"
                              onClick={() => {
                                setEditId(d.id);
                                setNewCategoryId(d.category.id);
                                setNewDiscountPercentage(d.discountPercentage);
                                setShowDiscountForm(true);
                              }}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="text-primary-600 hover:text-primary-700"
                              onClick={() => handleDeleteDiscount(d.id)}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">
            {"Carregando dados do cliente..."}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminCustomerDetailsPage;
